import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { fetchDashboard } from "../api/auth";
import { useAuth } from "../auth/AuthContext";
import { Metric, WorkQueue } from "../components/Metric";
import {
  Empty,
  ErrorNote,
  PageHeader,
  PageShell,
  Panel,
  VerificationLabel,
} from "../components/Page";

/**
 * The dashboard.
 *
 * Ordered by what a dealer opens this screen to find out: what needs doing
 * today. The work queue comes first and is the only amber on the page; the
 * measured figures sit below it on one band.
 *
 * A queue item is only listed when its number is live. "Open inquiries: 0"
 * would read as a business signal — buyers looked and did not write — so an
 * untracked figure is left out of the queue entirely rather than shown as zero.
 */

/** A bar chart of listings by status, drawn as SVG so it needs no chart library
 *  and no canvas. */
function StatusChart({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;

  const max = Math.max(...entries.map(([, count]) => count), 1);
  const W = 560;
  const H = 176;
  const pad = 8;
  const barW = Math.min(56, (W - pad * 2) / entries.length - 16);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      role="img"
      aria-label="Listings by status, as a bar chart"
    >
      {/* A baseline, so the bars stand on something rather than floating. */}
      <line
        x1={pad}
        x2={W - pad}
        y1={H - 24}
        y2={H - 24}
        className="stroke-line"
        strokeWidth={1}
      />
      {entries.map(([status, count], i) => {
        const x = pad + i * ((W - pad * 2) / entries.length) + 8;
        const h = Math.max(4, (count / max) * (H - 56));
        const y = H - 24 - h;
        return (
          <g key={status}>
            <motion.rect
              x={x}
              y={y}
              width={barW}
              height={h}
              rx={3}
              className="fill-primary"
              initial={{ opacity: 0, scaleY: 0 }}
              animate={{ opacity: 1, scaleY: 1 }}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.07 }}
              style={{ transformOrigin: `${x + barW / 2}px ${H - 24}px` }}
            />
            <text
              x={x + barW / 2}
              y={y - 8}
              textAnchor="middle"
              className="fill-ink text-[11px] font-semibold tnum"
            >
              {count}
            </text>
            <text
              x={x + barW / 2}
              y={H - 8}
              textAnchor="middle"
              className="fill-ink-muted text-[10px] capitalize"
            >
              {status.replace(/_/g, " ").toLowerCase()}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default function DashboardPage() {
  const { seller } = useAuth();

  const { data, isPending, error } = useQuery({
    queryKey: ["dashboard"],
    queryFn: fetchDashboard,
  });

  if (error)
    return (
      <PageShell>
        <ErrorNote error={error} />
      </PageShell>
    );

  const queue = data
    ? (
        [
          {
            key: "reconfirm",
            label: "Re-confirm availability",
            detail:
              "A listing drops off the public grid once its window lapses — 7 days for a private seller, 14 for a dealer.",
            count:
              data.coverage.freshness === "live"
                ? data.freshness.needingAvailabilityReconfirmation
                : 0,
            show: data.coverage.freshness === "live",
          },
          {
            key: "inquiries",
            label: "Open inquiries",
            detail: "Buyers who started a conversation about one of your listings.",
            count: data.coverage.inquiries === "live" ? data.inquiries.open : 0,
            show: data.coverage.inquiries === "live",
          },
          {
            key: "drafts",
            label: "Listings still in draft",
            detail: "Buyers cannot see a draft. Publish when the bike is ready to sell.",
            count: data.listingsByStatus.DRAFT ?? 0,
            show: true,
          },
        ] as const
      )
        .filter((item) => item.show && item.count > 0)
        // `show` was only ever the filter predicate; drop it before the rows
        // reach WorkQueue, which takes { label, detail, count }.
        .map(({ label, detail, count }) => ({ label, detail, count }))
    : [];

  return (
    <PageShell>
      <PageHeader
        eyebrow={seller ? seller.sellerType.replace(/_/g, " ").toLowerCase() : undefined}
        title={seller?.businessName ?? seller?.displayName ?? "Dashboard"}
        description={
          seller ? (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <VerificationLabel
                level={seller.verificationLevel}
                verifiedAt={seller.businessVerifiedAt ?? seller.identityVerifiedAt}
              />
              <span aria-hidden className="text-line-strong">
                ·
              </span>
              <span>Joined {new Date(seller.createdAt).toLocaleDateString()}</span>
            </span>
          ) : undefined
        }
        actions={
            <Link
              to="/vehicles/add"
              className="inline-flex min-h-10 items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-primary-deep hover:shadow-md"
            >
              <Plus className="h-4 w-4" aria-hidden />
              Add Vehicle
            </Link>
        }
      />

      <div className="space-y-4">
        {isPending ? (
          <p className="text-sm text-ink-muted">Loading your numbers…</p>
        ) : data ? (
          <>
            {/* The one amber element. Everything else stays ink, paper and petrol. */}
            <Panel
              title="Needs your attention"
              description="Work waiting on you, in the order it costs you money."
              action={
                queue.length > 0 ? (
                  <span className="text-xs font-medium text-signal-ink">
                    {queue.length} {queue.length === 1 ? "item" : "items"}
                  </span>
                ) : null
              }
            >
              <WorkQueue items={queue} />
            </Panel>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Metric
                label="Active listings"
                value={data.activeListings}
                coverage={data.coverage.activeListings}
              />
              <Metric label="Total views" value={data.views} coverage={data.coverage.views} />
              <Metric
                label="Inquiries"
                value={data.inquiries.total}
                coverage={data.coverage.inquiries}
                hint="buyers who wrote about a listing"
              />
              <Metric
                label="Contact releases"
                value={data.contactReleases}
                coverage={data.coverage.contactReleases}
                hint="buyers who shared their number"
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Panel
                title="Listings by status"
                action={
                  <Link
                    to="/vehicles/all"
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    Open listings
                    <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                  </Link>
                }
              >
                {Object.keys(data.listingsByStatus).length === 0 ? (
                  <Empty title="No listings yet">
                    Your first listing creates your seller profile automatically.
                  </Empty>
                ) : (
                  <StatusChart data={data.listingsByStatus} />
                )}
              </Panel>

              <Panel title="Freshness and responsiveness">
                <dl className="text-sm">
                  {[
                    {
                      label: "Need availability re-confirmation",
                      value: data.freshness.needingAvailabilityReconfirmation,
                      pending: data.coverage.freshness !== "live",
                    },
                    {
                      label: "Oldest active listing",
                      value:
                        data.freshness.oldestActiveListingDays === null
                          ? "—"
                          : `${data.freshness.oldestActiveListingDays} days`,
                      pending: data.coverage.freshness !== "live",
                    },
                    {
                      label: "Response rate",
                      value: data.responseRatePct === null ? "—" : `${data.responseRatePct}%`,
                      pending: data.coverage.responseRate !== "live",
                    },
                    {
                      label: "Average reply time",
                      value:
                        data.averageResponseMins === null
                          ? "—"
                          : `${data.averageResponseMins} min`,
                      pending: data.coverage.responseRate !== "live",
                    },
                  ].map((row) => (
                    <div
                      key={row.label}
                      className="flex items-baseline justify-between border-b border-line py-2.5 last:border-b-0"
                    >
                      <dt className="text-ink-muted">{row.label}</dt>
                      <dd
                        className="tnum font-medium text-ink"
                        data-pending={row.pending ? "true" : undefined}
                      >
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-4 text-xs leading-relaxed text-ink-muted">
                  A listing past its window is already off the public grid, so the re-confirmation
                  count is work waiting for you rather than lost income.
                </p>
              </Panel>
            </div>
          </>
        ) : null}
      </div>
    </PageShell>
  );
}
